import { DeviceInfoCell } from "../components/GridCells/DeviceInfoCell";
import { DisplayFormatCell } from "../components/GridCells/DisplayFormatCell";
import { TransformCell } from "../components/GridCells/TransformCell";
import {
  ModbusIPCell,
  ModbusMapIDCell,
  ModbusPortCell,
  ModbusRegisterCell,
  ModbusRegisterPLCCell,
  ModbusRegisterRTUCell,
  ModbusSlaveIdCell,
  SyncDirectionCell,
} from "../components/GridCells/ModbusCells";
import {
  LoSetpointCell,
  LoLoSetpointCell,
  HiSetpointCell,
  HiHiSetpointCell,
} from "../components/GridCells/SetpointCells";
import { CodeLegendCell } from "../components/GridCells/CodeLegendCell";
import { HiddenCell } from "../components/GridCells/HiddenCell";
import {
  HwioBoardNumberCell,
  HwioChannelNumberCell,
  HwioChannelNumberEditorCell,
  HwioIOTypeCell,
} from "../components/GridCells/HwioCells";
import { ParentTagCell } from "../components/GridCells/ParentTagCell";
import { ParentTagEditorCell } from "../components/GridCells/ParentTagEditorCell";
import { TextDisplayCell } from "../../common/components/DataGrid/components/defaultCellComponents";
import {
  generateCodeLegendCell,
  generateDeviceInfoCell,
  generateDisplayFormatCell,
  generateHiHiSetpointCell,
  generateHiSetpointCell,
  generateLoLoSetpointCell,
  generateLoSetpointCell,
  generateModbusIPCell,
  generateModbusMapIDCell,
  generateModbusPortCell,
  generateModbusRegisterCell,
  generateModbusSlaveIdCell,
  generateSyncDirectionCell,
  generateTransformCell,
  generateModbusRegisterPLCCell,
  generateModbusRegisterRTUCell,
  generateHiddenCell,
  generateSafetyShutdownCell,
  generateHwioIOTypeCell,
  generateHwioChannelNumberCell,
  generateHwioBoardNumberCell,
} from "./CellGenerators";
import { getLookupInternalNames } from "./Helpers";
import { isColumnCompatibleWithTag } from "./SourceTypeColumns";
import {
  createTextColumn,
  createBoolColumn,
  TextSelectEditor,
} from "../../common/components/DataGrid/DataGridHelper";

/**
 * Creates a definition of possible optional columns
 * @param {*} codeLegends
 * @param {*} allTags
 * @param {*} lookupValues The lookup values from LookupValueContext. Only dereferenced inside
 * valueGetter/editComponent, never at construction time, because optionalColumnLabels builds the
 * definitions with no arguments.
 * @returns
 */
export function createOptionalColumnDefs(codeLegends, allTags, lookupValues) {
  // Columns that only apply to certain source types cannot be opened for editing on a tag they do
  // not apply to. Which types those are lives in SourceTypeColumns.js, not here.
  const restrictedToSourceType =
    (columnId) =>
    ({ row }) =>
      isColumnCompatibleWithTag(columnId, row, lookupValues);

  return {
    DisplayName: createTextColumn("DisplayName", "Display Name"),
    AssetNumber: createTextColumn("AssetNumber", "Asset Number", {
      editable: false,
    }),
    DeviceInfo: {
      id: "DeviceInfo",
      accessorKey: "DeviceInfo",
      header: "Device Info",
      editable: false,
      valueGetter: ({ row }) => generateDeviceInfoCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <DeviceInfoCell row={row} lookupValues={lookupValues} />
      ),
    },
    MaxValue: createTextColumn("MaxValue", "Max Value"),
    MinValue: createTextColumn("MinValue", "Min Value"),
    Gain: createTextColumn("Gain", "Gain"),
    Offset: createTextColumn("Offset", "Offset"),
    AlarmLoLo: createTextColumn("AlarmLoLo", "Alarm LoLo"),
    AlarmLo: createTextColumn("AlarmLo", "Alarm Lo"),
    AlarmHiHi: createTextColumn("AlarmHiHi", "Alarm HiHi"),
    AlarmHi: createTextColumn("AlarmHi", "Alarm Hi"),
    DisplayFormat: {
      id: "DisplayFormat",
      accessorKey: "DisplayFormat",
      header: "Display Format",
      fieldType: "text",
      valueGetter: ({ row }) => generateDisplayFormatCell(row),
      displayComponent: ({ row }) => <DisplayFormatCell row={row} />,
    },
    UnitOfMeasure: createTextColumn("UnitOfMeasure", "Unit Of Measure"),
    LoggingFrequency: createTextColumn("LoggingFrequency", "Logging Frequency"),
    GroupName: createTextColumn("GroupName", "Group Name"),
    IsActive: createBoolColumn("IsActive", "Is Active"),
    Description: createTextColumn("Description", "Description"),
    IsWriteable: createBoolColumn("IsWriteable", "Is Writeable"),
    SyncToHub: createBoolColumn("SyncToHub", "Sync To Hub"),
    Transform: {
      id: "Transform",
      accessorKey: "Transform",
      header: "Transform",
      fieldType: "text",
      valueGetter: ({ row }) => generateTransformCell(row),
      displayComponent: ({ row }) => <TransformCell row={row} />,
    },
    ScanFrequency: createTextColumn("ScanFrequency", "Scan Frequency"),
    Hidden: {
      id: "Hidden",
      accessorKey: "Hidden",
      header: "Hidden",
      fieldType: "bool",
      valueGetter: ({ row }) => generateHiddenCell(row),
      displayComponent: ({ row }) => <HiddenCell row={row} />,
    },
    SafetyShutdown: {
      id: "SafetyShutdown",
      accessorKey: "SafetyShutdown",
      header: "Safety Shutdown",
      fieldType: "bool",
      valueGetter: ({ row }) => generateSafetyShutdownCell(row),
    },
    DynamicProperties: createTextColumn(
      "DynamicProperties",
      "Dynamic Properties"
    ),
    SourceType: {
      id: "SourceType",
      accessorKey: "SourceType",
      header: "Source Type",
      sortable: false,
      displayComponent: ({ row }) => <TextDisplayCell value={row.SourceType} />,
      editComponent: ({ value, onCommit }) => (
        <TextSelectEditor
          value={value}
          onCommit={onCommit}
          options={[
            "HardWiredIO",
            "J1939",
            "ModbusTCP",
            "ModbusRTU232",
            "ModbusRTU485",
            "SystemMetric",
            "Internal",
            "CDL",
            "FunctionLibrary",
            "MassaM3",
          ]}
        />
      ),
    },
    AlarmType: createTextColumn("AlarmType", "Alarm Type", { editable: false }),
    ModbusRegister: {
      id: "ModbusRegister",
      accessorKey: "ModbusRegister",
      header: "Modbus Register",
      fieldType: "text",
      valueGetter: ({ row }) => generateModbusRegisterCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <ModbusRegisterCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("ModbusRegister"),
    },
    DataType: createTextColumn("DataType", "Data Type"),
    ModbusIP: {
      id: "ModbusIP",
      accessorKey: "ModbusIP",
      header: "Modbus IP Address",
      fieldType: "text",
      valueGetter: ({ row }) => generateModbusIPCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <ModbusIPCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("ModbusIP"),
    },
    ModbusPort: {
      id: "ModbusPort",
      accessorKey: "ModbusPort",
      header: "Modbus Port",
      fieldType: "text",
      valueGetter: ({ row }) => generateModbusPortCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <ModbusPortCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("ModbusPort"),
    },
    ModbusSlaveId: {
      id: "ModbusSlave",
      accessorKey: "ModbusSlave",
      header: "Modbus Slave ID",
      fieldType: "text",
      valueGetter: ({ row }) => generateModbusSlaveIdCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <ModbusSlaveIdCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("ModbusSlave"),
    },
    HwioIOType: {
      id: "HwioIOType",
      accessorKey: "HwioIOType",
      header: "I/O Type (HWIO)",
      fieldType: "text",
      valueGetter: ({ row }) => generateHwioIOTypeCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <HwioIOTypeCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("HwioIOType"),
      editComponent: ({ value, onCommit, onCancel }) => (
        <TextSelectEditor
          value={value}
          onCommit={onCommit}
          onCancel={onCancel}
          options={getLookupInternalNames("TagType", lookupValues)}
          placeholder='I/O Type'
        />
      ),
    },
    HwioChannelNumber: {
      id: "HwioChannelNumber",
      accessorKey: "HwioChannelNumber",
      header: "Channel Number (HWIO)",
      fieldType: "text",
      valueGetter: ({ row }) =>
        generateHwioChannelNumberCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <HwioChannelNumberCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("HwioChannelNumber"),
      editComponent: ({ value, row, onCommit, onCancel }) => (
        <HwioChannelNumberEditorCell
          value={value}
          row={row}
          lookupValues={lookupValues}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      ),
    },
    HwioBoardNumber: {
      id: "HwioBoardNumber",
      accessorKey: "HwioBoardNumber",
      header: "Board Number (HWIO)",
      fieldType: "text",
      valueGetter: ({ row }) => generateHwioBoardNumberCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <HwioBoardNumberCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("HwioBoardNumber"),
    },
    DisplayOrder: createTextColumn("DisplayOrder", "Display Order"),
    SourceTagList: {
      id: "TagListName",
      accessorKey: "TagListName",
      header: "Source Tag List",
      editable: false,
      sortable: false,
    },
    ModbusMapID: {
      id: "ModbusMapID",
      accessorKey: "ModbusMapID",
      header: "Modbus Map ID",
      sortable: false,
      editable: false,
      valueGetter: ({ row }) => generateModbusMapIDCell(row),
      displayComponent: ({ row }) => <ModbusMapIDCell row={row} />,
    },
    AlarmDeadband: createTextColumn("AlarmDeadband", "Alarm Deadband"),
    AlarmTimeLimit: createTextColumn("AlarmTimeLimit", "Band Duration"),
    AlarmSeverity: createTextColumn("AlarmSeverity", "Alarm Severity", {
      editable: false,
    }),
    AlarmLimitRange: createTextColumn("AlarmLimitRange", "Alarm Limit Range", {
      editable: false,
    }),
    AlarmArmingDelay: createTextColumn("AlarmArmingDelay", "Arming Delay", {
      editable: false,
    }),
    AlarmArmingType: createTextColumn("AlarmArmingType", "Arming Type", {
      editable: false,
    }),
    ParentTagIdentifier: {
      ...createTextColumn("ParentTagIdentifier", "Parent Tag"),
      displayComponent: ({ row }) => (
        <ParentTagCell row={row} allTags={allTags} />
      ),
      editComponent: ({ value, row, onCommit, onCancel }) => (
        <ParentTagEditorCell
          value={value}
          row={row}
          allTags={allTags}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      ),
    },
    LoSetpoint: {
      id: "LoSetpoint",
      accessorKey: "LoSetpoint",
      header: "Lo Setpoint",
      sortable: false,
      fieldType: "text",
      valueGetter: ({ row }) => generateLoSetpointCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <LoSetpointCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("LoSetpoint"),
    },
    LoLoSetpoint: {
      id: "LoLoSetpoint",
      accessorKey: "LoLoSetpoint",
      header: "LoLo Setpoint",
      sortable: false,
      fieldType: "text",
      valueGetter: ({ row }) => generateLoLoSetpointCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <LoLoSetpointCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("LoLoSetpoint"),
    },
    HiSetpoint: {
      id: "HiSetpoint",
      accessorKey: "HiSetpoint",
      header: "Hi Setpoint",
      sortable: false,
      fieldType: "text",
      valueGetter: ({ row }) => generateHiSetpointCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <HiSetpointCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("HiSetpoint"),
    },
    HiHiSetpoint: {
      id: "HiHiSetpoint",
      accessorKey: "HiHiSetpoint",
      header: "HiHi Setpoint",
      sortable: false,
      fieldType: "text",
      valueGetter: ({ row }) => generateHiHiSetpointCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <HiHiSetpointCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("HiHiSetpoint"),
    },
    CodeLegend: {
      id: "CodeLegend",
      accessorKey: "CodeLegend",
      header: "Code Legend",
      editable: false,
      sortable: false,
      valueGetter: ({ row }) => generateCodeLegendCell(row, codeLegends),
      displayComponent: ({ row }) => (
        <CodeLegendCell row={row} codeLegends={codeLegends} />
      ),
    },
    SyncDirection: {
      id: "SyncDirection",
      accessorKey: "SyncDirection",
      header: "Sync Direction",
      sortable: false,
      valueGetter: ({ row }) => generateSyncDirectionCell(row, lookupValues),
      displayComponent: ({ row }) => (
        <SyncDirectionCell row={row} lookupValues={lookupValues} />
      ),
      isCellEditable: restrictedToSourceType("SyncDirection"),
      editComponent: ({ value, onCommit }) => (
        <TextSelectEditor
          value={value}
          onCommit={onCommit}
          options={["None", "Controller to Hub", "Bi-directional"]}
        />
      ),
    },
    AlarmId: createTextColumn("AlarmId", "Alarm ID", { sortable: false }),
    Alarm2Id: createTextColumn("Alarm2Id", "Alarm 2 ID", { sortable: false }),
    ModbusRegisterPLC: {
      id: "ModbusRegisterPLC",
      accessorKey: "ModbusRegisterPLC",
      header: "Modbus Register (PLC Addressing)",
      sortable: false,
      editable: false,
      valueGetter: ({ row }) => generateModbusRegisterPLCCell(row),
      displayComponent: ({ row }) => <ModbusRegisterPLCCell row={row} />,
    },
    ModbusRegisterRTU: {
      id: "ModbusRegisterRTU",
      accessorKey: "ModbusRegisterRTU",
      header: "Modbus Register (RTU Addressing)",
      sortable: false,
      editable: false,
      valueGetter: ({ row }) => generateModbusRegisterRTUCell(row),
      displayComponent: ({ row }) => <ModbusRegisterRTUCell row={row} />,
    },
  };
}

/**
 * Maps each optional column's field name to its pretty/display name (the same
 * `header` used for the grid's own column headers), for use anywhere a column
 * name needs to be shown to the user, e.g. the column-visibility select.
 */
export const optionalColumnLabels = Object.fromEntries(
  Object.entries(createOptionalColumnDefs()).map(([columnName, columnDef]) => [
    columnName,
    columnDef.header,
  ])
);
